import { useState, useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import { storeFormEntry } from "@/utils/formEntryStore";
import { submitFormToSupabase } from "@/utils/submitFormToSupabase";
import { defaultApplication, type AppSectionData } from "@/data/siteContent";

const GOOGLE_APP_FORM_ACTION = "https://docs.google.com/forms/d/e/1FAIpQLSftgaJHrWLVnF_eJVKcfsm87d5UHmmtLfL1f-NLCtGJPhzopg/formResponse";
const GOOGLE_APP_FIELDS = {
  name:          "entry.52376801",
  email:         "entry.92560383",
  phone:         "entry.1757746732",
  social:        "entry.955619724",
  artType:       "entry.1310552156",
  preferredSpace:"entry.799220116",
  years:         "entry.253418214",
  description:   "entry.710356906",
  portfolio:     "entry.1592110633",
};

interface ApplicationSectionProps { dataKey?: string; }

export default function ApplicationSection({ dataKey }: ApplicationSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [data, setData] = useAdminStorage<AppSectionData>(dataKey ?? "ma_application_v2", defaultApplication);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const set = useCallback(<K extends keyof AppSectionData>(key: K, val: AppSectionData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updateInfoCard = useCallback((i: number, field: "title" | "desc", val: string) => {
    const infoCards = data.infoCards.map((c, idx) => idx === i ? { ...c, [field]: val } : c);
    setData({ ...data, infoCards });
  }, [data, setData]);

  const removeInfoCard = useCallback((i: number) => {
    setData({ ...data, infoCards: data.infoCards.filter((_, idx) => idx !== i) });
  }, [data, setData]);

  const addInfoCard = useCallback(() => {
    const newCard = { icon: "ri-star-line", color: "text-pink-500", title: "New Benefit", desc: "Click to edit this description" };
    setData({ ...data, infoCards: [...data.infoCards, newCard] });
  }, [data, setData]);

  const updatePhoto = useCallback((i: number, src: string) => {
    const photos = data.photos.map((p, idx) => idx === i ? { ...p, src } : p);
    setData({ ...data, photos });
  }, [data, setData]);

  const updatePhotoPosition = useCallback((i: number, pos: string) => {
    const photos = data.photos.map((p, idx) => idx === i ? { ...p, objectPosition: pos } : p);
    setData({ ...data, photos });
  }, [data, setData]);

  const updatePhotoMobilePosition = useCallback((i: number, pos: string) => {
    const photos = data.photos.map((p, idx) => idx === i ? { ...p, mobileObjectPosition: pos } : p);
    setData({ ...data, photos });
  }, [data, setData]);

  const removePhoto = useCallback((i: number) => {
    setData({ ...data, photos: data.photos.filter((_, idx) => idx !== i) });
  }, [data, setData]);

  const addPhoto = useCallback(() => {
    setData({
      ...data,
      photos: [
        ...data.photos,
        {
          src: "",
          alt: "Artist space",
          objectPosition: "center center",
        },
      ],
    });
  }, [data, setData]);

  const updateField = useCallback((key: keyof AppSectionData["fields"], part: "label" | "placeholder", val: string) => {
    setData({ ...data, fields: { ...data.fields, [key]: { ...data.fields[key], [part]: val } } });
  }, [data, setData]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea[name='description']") as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setStatus("submitting");
    const fd = new FormData(form);
    const googlePayload = new URLSearchParams();
    googlePayload.set(GOOGLE_APP_FIELDS.name,           fd.get("fullName")?.toString()       ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.email,          fd.get("email")?.toString()          ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.phone,          fd.get("phone")?.toString()          ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.social,         fd.get("instagram")?.toString()      ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.artType,        fd.get("artCategory")?.toString()    ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.preferredSpace, fd.get("preferredSpace")?.toString() ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.years,          fd.get("yearsCreating")?.toString()  ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.description,    fd.get("description")?.toString()    ?? "");
    googlePayload.set(GOOGLE_APP_FIELDS.portfolio,      fd.get("portfolioUrl")?.toString()   ?? "");
    const appFields = [
      { label: "Full Name", value: fd.get("fullName")?.toString() ?? "" },
      { label: "Email", value: fd.get("email")?.toString() ?? "" },
      { label: "Phone", value: fd.get("phone")?.toString() ?? "" },
      { label: "Social Media", value: fd.get("instagram")?.toString() ?? "" },
      { label: "Art Category", value: fd.get("artCategory")?.toString() ?? "" },
      { label: "Preferred Space", value: fd.get("preferredSpace")?.toString() ?? "" },
      { label: "Years Creating", value: fd.get("yearsCreating")?.toString() ?? "" },
      { label: "Description", value: fd.get("description")?.toString() ?? "" },
      { label: "Portfolio URL", value: fd.get("portfolioUrl")?.toString() ?? "" },
    ];
    storeFormEntry("application", "Artist Application", appFields);
    submitFormToSupabase("application", "Artist Application", appFields).then((ok) => {
      if (!ok) console.error("[ApplicationSection] Form submission to Supabase failed");
    });

    try {
      await fetch(GOOGLE_APP_FORM_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: googlePayload.toString(),
      });
      setStatus("success");
      form.reset();
      setCharCount(0);
    } catch { setStatus("error"); }
  };

  const F = data.fields;

  const bg = isDark ? "bg-gray-900" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const inputCls = isDark
    ? "w-full border border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300";
  const selectCls = isDark
    ? "w-full border border-gray-600 bg-gray-700 text-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 cursor-pointer"
    : "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white cursor-pointer";
  const cardBg = isDark ? "bg-gray-700" : "bg-pink-50";
  const labelCls = isDark ? "text-gray-300" : "text-gray-700";

  return (
    <section id="application" className={`py-24 px-6 transition-colors duration-300 ${bg}`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left side */}
          <div className="w-full lg:w-2/5 flex-shrink-0">
            <div className={`rounded-3xl overflow-hidden relative ${isDark ? "bg-gray-800" : ""}`} style={isDark ? {} : { background: "linear-gradient(135deg, #f9a8d4 0%, #86efac 100%)" }}>
              <div className="p-8">
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {(data.photos || []).map((photo, i) => (
                    <div key={i} className="w-full h-28 rounded-xl overflow-hidden relative group/photo">
                      {isAdmin && (
                        <button
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 z-30 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer transition-colors shadow-sm"
                          title="Remove photo"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      )}
                      {isAdmin ? (
                        <AdminImageUpload
                          src={photo.src}
                          alt={photo.alt}
                          onReplace={(url) => updatePhoto(i, url)}
                          onDelete={() => removePhoto(i)}
                          className="w-full h-full"
                          imgClassName="w-full h-full object-cover"
                          objectPosition={photo.objectPosition || "center center"}
                          onPositionChange={(pos) => updatePhotoPosition(i, pos)}
                          mobileObjectPosition={(photo as typeof photo & { mobileObjectPosition?: string }).mobileObjectPosition ?? photo.objectPosition ?? "center center"}
                          onMobilePositionChange={(pos) => updatePhotoMobilePosition(i, pos)}
                        />
                      ) : (
                        <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" style={photo.objectPosition ? { objectPosition: photo.objectPosition } : undefined} />
                      )}
                    </div>
                  ))}
                  {isAdmin && data.photos.length < 4 && (
                    <button
                      onClick={addPhoto}
                      className="w-full h-28 rounded-xl border-2 border-dashed border-pink-200 hover:border-pink-400 text-pink-400 hover:text-pink-500 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors"
                      title="Add photo"
                    >
                      <i className="ri-add-line text-xl"></i>
                      <span className="text-xs font-semibold">Add Photo</span>
                    </button>
                  )}
                </div>
                {(isAdmin || data.quote) && (
                  isAdmin ? (
                    <AdminEditField value={data.quote} onSave={(v) => set("quote", v)} onDelete={() => set("quote", "")} multiline className="mb-2" inputClassName="italic text-white text-base">
                      <blockquote className="text-white text-base italic leading-relaxed">"{data.quote}"</blockquote>
                    </AdminEditField>
                  ) : (
                    <blockquote className="text-white text-base italic leading-relaxed">"{data.quote}"</blockquote>
                  )
                )}
                {(isAdmin || data.quoteAuthor) && (
                  isAdmin ? (
                    <AdminEditField value={data.quoteAuthor} onSave={(v) => set("quoteAuthor", v)} onDelete={() => set("quoteAuthor", "")} inputClassName="text-sm font-bold text-white/80">
                      <p className="text-white/80 text-sm font-bold">{data.quoteAuthor}</p>
                    </AdminEditField>
                  ) : (
                    <p className="text-white/80 text-sm font-bold mt-3">{data.quoteAuthor}</p>
                  )
                )}
              </div>
            </div>

            {/* Info cards */}
            <div className="mt-6 space-y-3">
              {(data.infoCards || []).map((item, i) => (
                <div key={i} className={`flex items-center gap-4 rounded-xl p-4 relative group/infocard ${cardBg}`}>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-white"}`}>
                    <i className={`${item.icon} ${item.color} text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    {isAdmin ? (
                      <>
                        <AdminEditField value={item.title} onSave={(v) => updateInfoCard(i, "title", v)} inputClassName="font-bold text-gray-800 text-sm">
                          <div className={`font-bold text-sm ${headingColor}`}>{item.title}</div>
                        </AdminEditField>
                        <AdminEditField value={item.desc} onSave={(v) => updateInfoCard(i, "desc", v)} inputClassName="text-xs">
                          <div className={`text-xs ${subColor}`}>{item.desc}</div>
                        </AdminEditField>
                      </>
                    ) : (
                      <>
                        <div className={`font-bold text-sm ${headingColor}`}>{item.title}</div>
                        <div className={`text-xs ${subColor}`}>{item.desc}</div>
                      </>
                    )}
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => removeInfoCard(i)}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer rounded-full hover:bg-red-50 opacity-0 group-hover/infocard:opacity-100 transition-opacity"
                      title="Remove card"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                </div>
              ))}
              {isAdmin && (
                <button
                  onClick={addInfoCard}
                  className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-pink-200 hover:border-pink-400 text-pink-400 hover:text-pink-500 rounded-xl py-3 text-sm font-semibold transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-lg"></i>
                  Add Info Card
                </button>
              )}
            </div>
          </div>

          {/* Right side — Form */}
          <div className="w-full lg:w-3/5">
            {(isAdmin || data.subheading) && (
              isAdmin ? (
                <AdminEditField value={data.subheading} onSave={(v) => set("subheading", v)} onDelete={() => set("subheading", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest">
                  <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.subheading}</span>
                </AdminEditField>
              ) : (
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.subheading}</span>
              )
            )}
            {(isAdmin || data.heading) && (
              isAdmin ? (
                <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mt-2 mb-2" inputClassName="text-4xl font-extrabold">
                  <h2 className={`text-4xl font-extrabold mt-2 mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
                </AdminEditField>
              ) : (
                <h2 className={`text-4xl font-extrabold mt-2 mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
              )
            )}
            {(isAdmin || data.body) && (
              isAdmin ? (
                <AdminEditField value={data.body} onSave={(v) => set("body", v)} onDelete={() => set("body", "")} multiline richText className="mb-8" inputClassName="text-gray-500">
                  <RichText html={data.body} as="p" className={`mb-8 ${subColor}`} />
                </AdminEditField>
              ) : (
                <RichText html={data.body} as="p" className={`mb-8 ${subColor}`} />
              )
            )}

            {status === "success" ? (
              <div className={`border rounded-2xl p-10 text-center ${isDark ? "bg-gray-800 border-gray-700" : "bg-green-50 border-green-200"}`}>
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-green-500 text-3xl"></i>
                </div>
                {isAdmin ? (
                  <>
                    <AdminEditField value={data.successTitle} onSave={(v) => set("successTitle", v)} className="mb-2" inputClassName="text-2xl font-bold text-center">
                      <h3 className={`text-2xl font-bold mb-2 ${headingColor}`}>{data.successTitle}</h3>
                    </AdminEditField>
                    <AdminEditField value={data.successBody} onSave={(v) => set("successBody", v)} multiline inputClassName="text-center">
                      <p className={subColor}>{data.successBody}</p>
                    </AdminEditField>
                  </>
                ) : (
                  <>
                    <h3 className={`text-2xl font-bold mb-2 ${headingColor}`}>{data.successTitle}</h3>
                    <p className={subColor}>{data.successBody}</p>
                  </>
                )}
                <button onClick={() => setStatus("idle")} className="mt-6 bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap">
                  Submit Another Application
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="artist-application-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.fullName.label} onSave={(v) => updateField("fullName", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.fullName.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.fullName.label}</label>
                    )}
                    <div className="relative">
                      <input type="text" name="fullName" required placeholder={F.fullName.placeholder} className={inputCls} />
                      {isAdmin && (
                        <AdminEditField value={F.fullName.placeholder} onSave={(v) => updateField("fullName", "placeholder", v)} className="absolute right-2 top-1/2 -translate-y-1/2" inputClassName="text-xs text-gray-400">
                          <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                        </AdminEditField>
                      )}
                    </div>
                  </div>
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.email.label} onSave={(v) => updateField("email", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.email.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.email.label}</label>
                    )}
                    <div className="relative">
                      <input type="email" name="email" required placeholder={F.email.placeholder} className={inputCls} />
                      {isAdmin && (
                        <AdminEditField value={F.email.placeholder} onSave={(v) => updateField("email", "placeholder", v)} className="absolute right-2 top-1/2 -translate-y-1/2" inputClassName="text-xs text-gray-400">
                          <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                        </AdminEditField>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.phone.label} onSave={(v) => updateField("phone", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.phone.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.phone.label}</label>
                    )}
                    {isAdmin ? (
                      <AdminEditField value={data.phoneHelperText} onSave={(v) => set("phoneHelperText", v)} className="mt-1 mb-1.5" inputClassName="text-xs text-gray-400">
                        <p className={`text-xs mt-1 mb-1.5 ${subColor}`}>{data.phoneHelperText}</p>
                      </AdminEditField>
                    ) : (
                      <p className={`text-xs mt-1 mb-1.5 ${subColor}`}>{data.phoneHelperText}</p>
                    )}
                    <div className="relative">
                      <input type="tel" name="phone" placeholder={F.phone.placeholder} className={inputCls} />
                      {isAdmin && (
                        <AdminEditField value={F.phone.placeholder} onSave={(v) => updateField("phone", "placeholder", v)} className="absolute right-2 top-1/2 -translate-y-1/2" inputClassName="text-xs text-gray-400">
                          <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                        </AdminEditField>
                      )}
                    </div>
                  </div>
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.instagram.label} onSave={(v) => updateField("instagram", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.instagram.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.instagram.label}</label>
                    )}
                    {isAdmin ? (
                      <AdminEditField value={data.instagramHelperText} onSave={(v) => set("instagramHelperText", v)} className="mt-1 mb-1.5" inputClassName="text-xs text-gray-400">
                        <p className={`text-xs mt-1 mb-1.5 ${subColor}`}>{data.instagramHelperText}</p>
                      </AdminEditField>
                    ) : (
                      <p className={`text-xs mt-1 mb-1.5 ${subColor}`}>{data.instagramHelperText}</p>
                    )}
                    <div className="relative">
                      <input type="text" name="instagram" placeholder={F.instagram.placeholder} className={inputCls} />
                      {isAdmin && (
                        <AdminEditField value={F.instagram.placeholder} onSave={(v) => updateField("instagram", "placeholder", v)} className="absolute right-2 top-1/2 -translate-y-1/2" inputClassName="text-xs text-gray-400">
                          <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                        </AdminEditField>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className={`block text-sm font-semibold ${labelCls}`}>Art Category *</label>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            const val = prompt("Add new art category:");
                            if (val && val.trim()) set("artCategories", [...data.artCategories, val.trim()]);
                          }}
                          className="text-xs text-pink-400 hover:text-pink-600 cursor-pointer whitespace-nowrap flex items-center gap-0.5"
                        >
                          <i className="ri-add-line"></i> Add
                        </button>
                      )}
                    </div>
                    <select name="artCategory" required className={selectCls}>
                      <option value="">Select a category</option>
                      {data.artCategories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {isAdmin && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {data.artCategories.map((c, ci) => (
                          <span key={c} className="inline-flex items-center gap-1 bg-pink-50 text-pink-600 text-xs px-2 py-0.5 rounded-full">
                            {c}
                            <button
                              type="button"
                              onClick={() => set("artCategories", data.artCategories.filter((_, i) => i !== ci))}
                              className="text-red-400 hover:text-red-600 cursor-pointer"
                              title="Remove"
                            >
                              <i className="ri-close-line" style={{ fontSize: "10px" }}></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className={`block text-sm font-semibold ${labelCls}`}>Preferred Space *</label>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            const val = prompt("Add new space type:");
                            if (val && val.trim()) set("spaceTypes", [...data.spaceTypes, val.trim()]);
                          }}
                          className="text-xs text-pink-400 hover:text-pink-600 cursor-pointer whitespace-nowrap flex items-center gap-0.5"
                        >
                          <i className="ri-add-line"></i> Add
                        </button>
                      )}
                    </div>
                    <select name="preferredSpace" required className={selectCls}>
                      <option value="">Select a space</option>
                      {data.spaceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {isAdmin && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {data.spaceTypes.map((s, si) => (
                          <span key={s} className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded-full">
                            {s}
                            <button
                              type="button"
                              onClick={() => set("spaceTypes", data.spaceTypes.filter((_, i) => i !== si))}
                              className="text-red-400 hover:text-red-600 cursor-pointer"
                              title="Remove"
                            >
                              <i className="ri-close-line" style={{ fontSize: "10px" }}></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {isAdmin ? (
                    <AdminEditField value={F.yearsCreating.label} onSave={(v) => updateField("yearsCreating", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.yearsCreating.label}</label>
                    </AdminEditField>
                  ) : (
                    <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.yearsCreating.label}</label>
                  )}
                  <div className="relative">
                    <input type="number" name="yearsCreating" required min="0" max="100" placeholder={F.yearsCreating.placeholder} className={inputCls} />
                    {isAdmin && (
                      <AdminEditField value={F.yearsCreating.placeholder} onSave={(v) => updateField("yearsCreating", "placeholder", v)} className="absolute right-2 top-1/2 -translate-y-1/2" inputClassName="text-xs text-gray-400">
                        <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                      </AdminEditField>
                    )}
                  </div>
                </div>
                <div>
                  {isAdmin ? (
                    <AdminEditField value={F.description.label} onSave={(v) => updateField("description", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>
                        {F.description.label}{" "}
                        <span className={`font-normal ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>({charCount}/500)</span>
                      </label>
                    </AdminEditField>
                  ) : (
                    <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>
                      {F.description.label}{" "}
                      <span className={`font-normal ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>({charCount}/500)</span>
                    </label>
                  )}
                  <div className="relative">
                    <textarea name="description" required rows={4} maxLength={500} placeholder={F.description.placeholder} className={`${inputCls} resize-none`} onChange={(e) => setCharCount(e.target.value.length)} />
                    {isAdmin && (
                      <AdminEditField value={F.description.placeholder} onSave={(v) => updateField("description", "placeholder", v)} className="absolute right-2 top-2" inputClassName="text-xs text-gray-400">
                        <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                      </AdminEditField>
                    )}
                  </div>
                </div>
                <div>
                  {isAdmin ? (
                    <AdminEditField value={F.portfolio.label} onSave={(v) => updateField("portfolio", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold">
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.portfolio.label}</label>
                    </AdminEditField>
                  ) : (
                    <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.portfolio.label}</label>
                  )}
                  <div className="relative">
                    <input type="url" name="portfolioUrl" placeholder={F.portfolio.placeholder} className={inputCls} />
                    {isAdmin && (
                      <AdminEditField value={F.portfolio.placeholder} onSave={(v) => updateField("portfolio", "placeholder", v)} className="absolute right-2 top-1/2 -translate-y-1/2" inputClassName="text-xs text-gray-400">
                        <span className="text-xs bg-pink-100 text-pink-400 px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap">placeholder</span>
                      </AdminEditField>
                    )}
                  </div>
                </div>
                {/* Rules acknowledgment */}
                <div className={`border rounded-xl p-4 ${isDark ? "bg-gray-800 border-gray-600" : "bg-pink-50 border-pink-200"}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="rulesAcknowledged"
                      required
                      className="mt-0.5 w-4 h-4 accent-pink-500 flex-shrink-0 cursor-pointer"
                    />
                    {isAdmin ? (
                      <AdminEditField
                        value={data.rulesAcknowledgmentText}
                        onSave={(v) => set("rulesAcknowledgmentText", v)}
                        multiline
                        inputClassName="text-sm leading-relaxed"
                      >
                        <span className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {data.rulesAcknowledgmentText.includes("Rules & Guidelines") ? (
                            <>
                              {data.rulesAcknowledgmentText.split("Rules & Guidelines")[0]}
                              <button
                                type="button"
                                onClick={() => document.querySelector("#rules")?.scrollIntoView({ behavior: "smooth" })}
                                className="text-pink-500 font-semibold underline underline-offset-2 hover:text-pink-700 cursor-pointer whitespace-nowrap"
                              >
                                Rules &amp; Guidelines
                              </button>
                              {data.rulesAcknowledgmentText.split("Rules & Guidelines")[1]}
                            </>
                          ) : data.rulesAcknowledgmentText}
                        </span>
                      </AdminEditField>
                    ) : (
                      <span className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {data.rulesAcknowledgmentText.includes("Rules & Guidelines") ? (
                          <>
                            {data.rulesAcknowledgmentText.split("Rules & Guidelines")[0]}
                            <button
                              type="button"
                              onClick={() => document.querySelector("#rules")?.scrollIntoView({ behavior: "smooth" })}
                              className="text-pink-500 font-semibold underline underline-offset-2 hover:text-pink-700 cursor-pointer whitespace-nowrap"
                            >
                              Rules &amp; Guidelines
                            </button>
                            {data.rulesAcknowledgmentText.split("Rules & Guidelines")[1]}
                          </>
                        ) : data.rulesAcknowledgmentText}
                      </span>
                    )}
                  </label>
                </div>

                {status === "error" && <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>}
                <div className="relative">
                  <button type="submit" disabled={status === "submitting"} className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap text-base">
                    {status === "submitting" ? <><i className="ri-loader-4-line animate-spin"></i>Submitting...</> : (
                      <>
                        {isAdmin ? (
                          <AdminEditField value={data.submitLabel} onSave={(v) => set("submitLabel", v)} inputClassName="text-white font-bold text-base text-center">
                            <span>{data.submitLabel}</span>
                          </AdminEditField>
                        ) : (
                          <span>{data.submitLabel}</span>
                        )}
                        <i className="ri-arrow-right-line text-lg"></i>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
